import { useQuery } from "@tanstack/react-query";
import { use, useState } from "react";

import { getEarningsByDay } from "@/backend/focus/admin-ui";
import { EarningsGraph } from "@/components/shared/earnings-graphs";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/user-context";
import { formatUSDCents } from "@/utils/currency";

const ChartFilterDayControl = ({
  onButtonClick,
  label,
  afterDaysAgo,
  selectedAfterDaysAgo,
}: {
  label: string;
  selectedAfterDaysAgo: number;
  afterDaysAgo: number;
  onButtonClick: (days: number) => void;
}) => {
  const isActive = selectedAfterDaysAgo === afterDaysAgo;

  return (
    <Button
      size="xs"
      variant={isActive ? "default" : "outline"}
      onClick={() => onButtonClick(afterDaysAgo)}
    >
      {label}
    </Button>
  );
};

export const DiscoverEarningsChart = ({ publicKey }: { publicKey: string }) => {
  const [afterDaysAgo, setAfterDaysAgo] = useState(30);
  const { currentUserPublicKey } = use(UserContext);
  const { data: earnings = {} } = useQuery({
    queryKey: [
      "discover-earnings-by-day",
      publicKey,
      currentUserPublicKey,
      afterDaysAgo,
    ],
    queryFn: () =>
      getEarningsByDay({
        afterDaysAgo,
        publicKey,
        currentUserPublicKey,
      }),
    enabled: !!publicKey && !!currentUserPublicKey,
    placeholderData: (previousData) => previousData ?? {},
    staleTime: 1000 * 60,
  });

  const onButtonClick = (days: number) => {
    setAfterDaysAgo(days);
  };

  const earnedUSDCents = Object.values(earnings).reduce(
    (acc, val) => acc + val,
    0,
  );

  return (
    <div className="border-t border-border-light p-0 transition-all">
      <div
        className="
          mt-4 flex flex-col items-center justify-between gap-4 px-4
          md:flex-row md:gap-0
        "
      >
        <div className="text-xs text-muted">
          <span className="font-shadow-green font-mono text-emerald-400">
            +{formatUSDCents(earnedUSDCents)}
          </span>{" "}
          <br />
          (last {afterDaysAgo}d)
        </div>
        <div
          className="
            grid grid-cols-2 gap-4
            md:grid-cols-4 md:gap-2
          "
        >
          <ChartFilterDayControl
            onButtonClick={onButtonClick}
            label="Last 24hrs"
            afterDaysAgo={1}
            selectedAfterDaysAgo={afterDaysAgo}
          />
          <ChartFilterDayControl
            onButtonClick={onButtonClick}
            label="Last 7d"
            afterDaysAgo={7}
            selectedAfterDaysAgo={afterDaysAgo}
          />
          <ChartFilterDayControl
            onButtonClick={onButtonClick}
            label="Last 30d"
            afterDaysAgo={30}
            selectedAfterDaysAgo={afterDaysAgo}
          />
          <ChartFilterDayControl
            onButtonClick={onButtonClick}
            label="Last 90d"
            afterDaysAgo={90}
            selectedAfterDaysAgo={afterDaysAgo}
          />
        </div>
      </div>
      <div className="py-4" />
      <EarningsGraph height={160} earnings={earnings} />
    </div>
  );
};
